import React, { Component } from 'react';
import Select from 'react-select';
import {
  Alert,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Row,
  Col,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { fetchUserDetailsByPhone, submitOrderId } from '../../services/api/cashless-vision';
import { fetchUsersRelative } from '../../services/api/offline-consult';
import verifiedIcon from '../../images/verifiedIcon.svg';

class CreateOrder extends Component {
  constructor(props) {
    super(props);
    this.state = {
      modalVisible: false,
      userId: '',
      phone: '',
      relativeResult: [],
      relativeLoading: false,
      showFormWarning: false,
      alertMessage: 'None',
      loading: false,
      name: '',
      email: '',
      voucherCode: '',
      walletBalance: 0,
      verified: false,
      selectedRelative: null,
      orderIdModalVisible: false,
      orderId: '',
    };
  }

  //  Restore modal state if user reloads page
  componentDidMount() {
    const savedData = localStorage.getItem('orderModalData');
    if (savedData) {
      const parsed = JSON.parse(savedData);
      // Restore Create Order modal first, then Order ID modal after a tick to ensure stacking order
      if (parsed.modalVisible) {
        this.setState({
          modalVisible: true,
          phone: parsed.phone || '',
        }, () => {
          if (parsed.orderIdModalVisible) {
            setTimeout(() => {
              this.setState({
                orderIdModalVisible: true,
                orderId: parsed.orderId || '',
              });
            }, 0);
          }
        });
      } else if (parsed.orderIdModalVisible) {
        this.setState({
          orderIdModalVisible: true,
          phone: parsed.phone || '',
          orderId: parsed.orderId || '',
        });
      }
    }
  }

  resetState = () => {
    this.setState({
      userId: '',
      phone: '',
      relativeResult: [],
      relativeLoading: false,
      showFormWarning: false,
      alertMessage: 'None',
      loading: false,
      name: '',
      email: '',
      voucherCode: '',
      walletBalance: 0,
      verified: false,
      selectedRelative: null,
    });
  };

  isValidPhoneNumber = (phone) => {
    const regex = /^[6-9]\d{9}$/;
    return regex.test(phone);
  };

  handleInputChange = (field, value) => {
    if (field === 'phone' && value !== this.state.phone) {
      // Reset fields if phone number changes
      this.resetState();
    }
    this.setState({ [field]: value });
  };

  handleVerify = async () => {
    const { phone } = this.state;

    if (!this.isValidPhoneNumber(phone)) {
      this.setState({
        showFormWarning: true,
        alertMessage: 'Please enter a valid Indian phone number'
      });
      return;
    }

    this.setState({
      relativeLoading: true
    });

    try {
      const userDetailsResponse = await fetchUserDetailsByPhone(phone);
      if (userDetailsResponse.message === 'success') {
        const { userId, userName, userEmail, voucherCode, walletBalance } = userDetailsResponse.data;
        this.setState({
          userId,
          name: userName,
          email: userEmail,
          voucherCode,
          walletBalance: walletBalance || 0,
          verified: true
        });
        toast.success('User details fetched successfully!');

        const relativesResponse = await fetchUsersRelative(userId);
        const relatives = relativesResponse.data.map((relative) => ({
          value: relative.relativeId,
          label: relative.relativeName,
        }));
        this.setState({ relativeResult: relatives });
      } else {
        toast.error(userDetailsResponse.errorMessage);
        // Only reset fields except phone so phone remains in modal
        this.setState(prevState => ({
          userId: '',
          relativeResult: [],
          relativeLoading: false,
          showFormWarning: false,
          alertMessage: 'None',
          loading: false,
          name: '',
          email: '',
          voucherCode: '',
          walletBalance: 0,
          verified: false,
          selectedRelative: null,
          orderIdModalVisible: true,
          modalVisible: true, // NOTE: KEEP the Create Modal open
          phone: prevState.phone, // preserve phone
        }), () => {
          //  Save both modals' state and data to localStorage for persistence
          localStorage.setItem('orderModalData', JSON.stringify({
            orderIdModalVisible: true,
            modalVisible: true,
            phone: this.state.phone,
            orderId: this.state.orderId
          }));
        });

      }
    } catch (error) {
      console.log(error);
      toast.error(error.response?.data?.message || 'Failed to fetch user details');
      this.resetState();
    } finally {
      this.setState({
        relativeLoading: false
      });
    }
  };

  handleRelativeChange = async (selectedOption) => {
    this.setState({ selectedRelative: selectedOption });

    try {
      const response = await fetchUserDetailsByPhone(this.state.phone, selectedOption?.value || null);
      const { walletBalance } = response.data;
      this.setState({
        walletBalance: walletBalance || 0
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch wallet details');
    }
  };

  placeRequest = async (status, rejectionReason = null) => {
    const { userId, selectedRelative, voucherCode, name, email, phone, walletBalance } = this.state;
    const relativeId = selectedRelative ? selectedRelative.value : null;

    this.setState({ loading: true });

    try {
      const result = await this.props.submit(userId, relativeId, voucherCode, status, rejectionReason);
      if (result) {
        this.resetState();
        this.setState({ modalVisible: false });
      }
    } catch {
      toast.error('Failed to create order');
    } finally {
      this.setState({ loading: false });
    }
  };

  toggleModal = () => {
    this.setState(prevState => {
      const newVisible = !prevState.modalVisible;
      if (!newVisible) {
        this.resetState(); // Reset state when closing the modal
        localStorage.removeItem('orderModalData');
      }
      return { modalVisible: newVisible };
    });
  };

  //  Submit Order ID and clear persisted data
  handleOrderIdSubmit = async () => {
    const { orderId, phone } = this.state;
    try {
      const response = await submitOrderId(phone, orderId);
      if (response) {
        toast.success('Order Id submitted successfully');
        this.setState({
          orderIdModalVisible: false,
          orderId: '',
        }, () => {
          //  Clear only Order ID modal data from localStorage
          const savedData = localStorage.getItem('orderModalData');
          if (savedData) {
            const parsed = JSON.parse(savedData);
            localStorage.setItem('orderModalData', JSON.stringify({
              ...parsed,
              orderIdModalVisible: false,
              orderId: '',
            }));
          }
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit Order Id');
    }
  };

  render() {
    const {
      modalVisible,
      phone,
      name,
      email,
      voucherCode,
      walletBalance,
      relativeResult,
      loading,
      relativeLoading,
      showFormWarning,
      alertMessage,
      verified,
      selectedRelative,
      orderIdModalVisible,
      orderId,
    } = this.state;

    return (
      <div>
        <button
          type="button"
          style={{
            background: '#714FFF',
            borderColor: '#714FFF',
            width: '106%',
            margin: '0px',
            borderRadius: '5px',
            border: '0px',
            color: 'white',
            padding: '12px 12px',
            fontSize: '14px',
            fontWeight: '600',
            fontFamily: 'Inter, sans-serif',
            justifyContent: 'flex-start',
          }}
          onClick={this.toggleModal}
        >
          Create Order
        </button>

        {/*  Order ID Modal (persistent) */}
        <Modal
          centered
          isOpen={orderIdModalVisible}
          toggle={null}
          backdrop="static"
          keyboard={false}
          style={{ zIndex: 2000 }}
        >
          <ModalHeader>
            <span style={{ color: '#714FFF', fontSize: '16px', fontWeight: '600', fontFamily: 'Inter, sans-serif' }}>
              Not Registered User - Enter Order Id
            </span>
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Row>
                <Col className="col-3">
                  <Label style={{ fontFamily: 'Inter, sans-serif' }}>Phone Number :</Label>
                </Col>
                <Col>
                  <input
                    className="form-control"
                    value={phone}
                    readOnly
                    style={{ backgroundColor: 'transparent', borderColor: '#ced4da' }}
                  />
                </Col>
              </Row>
            </FormGroup>
            <FormGroup>
              <Row>
                <Col className="col-3">
                  <Label style={{ fontFamily: 'Inter, sans-serif' }}>Order Id :</Label>
                </Col>
                <Col>
                  <input
                    type='number'
                    className="form-control"
                    value={orderId}
                    onChange={e => {
                      const newOrderId = e.target.value;
                      this.setState({ orderId: newOrderId });
                    }}
                    onBlur={e => {
                      const newOrderId = e.target.value;
                      let savedData = {};
                      try {
                        const raw = localStorage.getItem('orderModalData');
                        if (raw) {
                          savedData = JSON.parse(raw);
                        }
                      } catch (e) {
                        savedData = {};
                      }
                      localStorage.setItem('orderModalData', JSON.stringify({
                        ...savedData,
                        orderId: newOrderId,
                      }));
                    }}
                    placeholder="Enter Order Id"
                  />
                </Col>
              </Row>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              style={{
                backgroundColor: '#714FFF',
                fontWeight: '600',
                borderRadius: '8px',
              }}
              onClick={() => {
                this.handleOrderIdSubmit();
              }}
              disabled={!orderId}
            >
              Submit
            </Button>
          </ModalFooter>
        </Modal>

        {/*  Create Order Modal */}
        <Modal centered isOpen={modalVisible} toggle={this.toggleModal}>
          <ModalHeader
            toggle={this.toggleModal}
            style={{
              borderBottom: '1px solid #E0E0E0'
            }}
          >
            <span style={{
              color: '#714FFF',
              fontSize: '16px',
              fontWeight: '600',
              fontFamily: 'Inter, sans-serif'
            }}>
              Create Order
            </span>
          </ModalHeader>
          <ModalBody>
            <form>
              <FormGroup>
                <Row>
                  <Col className="col-3">
                    <Label style={{ fontFamily: 'Inter, sans-serif' }}>Number:</Label>
                  </Col>
                  <Col>
                    <div style={{ position: 'relative' }}>
                      <input
                        className="form-control"
                        value={phone}
                        onChange={(e) => this.handleInputChange('phone', e.target.value)}
                        style={{
                          borderColor: verified ? '#ced4da' : 'red',
                        }}
                      />
                      {verified ? (
                        <div
                          style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            backgroundColor: '#86CA4B',
                            color: '#FFFFFF',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            padding: '2px 8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            zIndex: 1
                          }}
                        >
                          <img src={verifiedIcon} alt="Verified" style={{ width: '16px', height: '16px' }} />
                          Verified
                        </div>
                      ) : (
                        <Button
                          onClick={this.handleVerify}
                          style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            backgroundColor: '#714FFF',
                            color: '#FFFFFF',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            padding: '2px 8px',
                            border: 'none',
                            margin: 0
                          }}
                        >
                          Verify
                        </Button>
                      )}
                    </div>
                  </Col>
                </Row>
              </FormGroup>

              {/* Other input fields */}
              <FormGroup>
                <Row>
                  <Col className="col-3">
                    <Label style={{ fontFamily: 'Inter, sans-serif' }}>Name:</Label>
                  </Col>
                  <Col>
                    <input
                      className="form-control"
                      value={name}
                      readOnly
                      style={{ backgroundColor: 'transparent', borderColor: '#ced4da' }}
                    />
                  </Col>
                </Row>
              </FormGroup>

              <FormGroup>
                <Row>
                  <Col className="col-3">
                    <Label style={{ fontFamily: 'Inter, sans-serif' }}>Email ID:</Label>
                  </Col>
                  <Col>
                    <input
                      className="form-control"
                      value={email}
                      readOnly
                      style={{ backgroundColor: 'transparent', borderColor: '#ced4da' }}
                    />
                  </Col>
                </Row>
              </FormGroup>

              <FormGroup>
                <Row>
                  <Col className="col-3">
                    <Label style={{ fontFamily: 'Inter, sans-serif' }}>Voucher Code:</Label>
                  </Col>
                  <Col>
                    <input
                      className="form-control"
                      value={voucherCode}
                      readOnly
                      style={{ backgroundColor: 'transparent', borderColor: '#ced4da' }}
                    />
                  </Col>
                </Row>
              </FormGroup>

              <FormGroup>
                <Row>
                  <Col className="col-3">
                    <Label style={{ fontFamily: 'Inter, sans-serif' }}>Dependents:</Label>
                  </Col>
                  <Col>
                    <Select
                      isSearchable={false}
                      isClearable={true}
                      value={selectedRelative}
                      onChange={this.handleRelativeChange}
                      options={relativeResult}
                      isLoading={relativeLoading}
                    />
                  </Col>
                </Row>
              </FormGroup>

              <FormGroup>
                <Row>
                  <Col className="col-3">
                    <Label style={{ fontFamily: 'Inter, sans-serif' }}>Wallet Balance:</Label>
                  </Col>
                  <Col>
                    <div
                      style={{
                        borderRadius: '20px',
                        padding: '8px 12px',
                        backgroundColor: 'rgba(113, 79, 255, 0.05)',
                        color: '#714FFF',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      {`₹${walletBalance}`}
                    </div>
                  </Col>
                </Row>
              </FormGroup>
            </form>

            <Alert color="warning" isOpen={showFormWarning} toggle={() => this.setState({ showFormWarning: false })}>
              {alertMessage}
            </Alert>
          </ModalBody>

          <ModalFooter
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px',
              padding: '20px',
            }}
          >
            <div
              style={{
                backgroundColor: 'rgb(232, 248, 239)',
                color: '#585969',
                padding: '12px 16px',
                borderRadius: '8px',
                textAlign: 'center',
                font: 'Inter',
                fontSize: '14px',
                width: '100%',
                fontWeight: '500',
              }}
            >
              Tap on the button below to deduct the amount from the user's wallet and trigger a WhatsApp message to
              complete the payment.
            </div>

            <Button
              onClick={() => this.placeRequest('received')}
              disabled={!verified || loading}
              style={{
                backgroundColor: '#714FFF',
                border: 'none',
                width: '100%',
                padding: '12px',
                fontWeight: '600',
                borderRadius: '8px',
                margin: '0',
              }}
            >
              {loading ? 'Processing...' : 'Proceed'}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    );
  }
}

export default CreateOrder;
